import { useEffect, useState } from 'react';
import { ArrowLeft, Plus, Save, Trash2 } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApiClient } from '../../lib/apiClient';
import { useI18n } from '../../i18n/LanguageContext';
import type { ManualTestDefinition, ManualTestFieldDefinition } from '../../lib/manualTests';
import { SelectMenu } from '../../ui/SelectMenu';

const manualTestEditorItOverrides: Record<string, string> = {
  'Manual test definition not found.': 'Definizione test manuale non trovata.',
  'Failed to load manual test definitions.': 'Impossibile caricare le definizioni dei test manuali.',
  'Definition name is required.': 'Il nome della definizione è obbligatorio.',
  'A valid code is required.': 'È richiesto un codice valido.',
  'Each custom field must have both a label and an ID.': 'Ogni campo personalizzato deve avere sia etichetta sia ID.',
  'A manual test with this code already exists.': 'Esiste già un test manuale con questo codice.',
  'Manual test definition created.': 'Definizione test manuale creata.',
  'Manual test definition updated.': 'Definizione test manuale aggiornata.',
  'Failed to save definitions.': 'Impossibile salvare le definizioni.',
  'Add Manual Test': 'Aggiungi test manuale',
  'Edit Manual Test': 'Modifica test manuale',
  'Create a new manual test definition on its own page.': 'Crea una nuova definizione di test manuale nella sua pagina dedicata.',
  'Update the selected manual test definition on its own page.': 'Aggiorna la definizione di test manuale selezionata nella sua pagina dedicata.',
  'Saving...': 'Salvataggio...',
  'Create Test': 'Crea test',
  'Save Changes': 'Salva modifiche',
  'Loading definition...': 'Caricamento definizione...',
  Code: 'Codice',
  Name: 'Nome',
  Category: 'Categoria',
  'Value Label': 'Etichetta valore',
  'Default Unit': 'Unità predefinita',
  'Requires image upload': 'Richiede caricamento immagine',
  Description: 'Descrizione',
  'Short usage notes for clinicians': 'Brevi note d’uso per i clinici',
  'AI Prompt (optional)': 'Prompt AI (facoltativo)',
  'Custom Fields': 'Campi personalizzati',
  'Configure extra values captured for this manual test.': 'Configura i valori extra acquisiti per questo test manuale.',
  'Add Field': 'Aggiungi campo',
  'No custom fields for this test.': 'Nessun campo personalizzato per questo test.',
  'Field ID': 'ID campo',
  Label: 'Etichetta',
  Type: 'Tipo',
  Unit: 'Unità',
  Hint: 'Suggerimento',
  'Shown to staff during entry': "Mostrato al personale durante l'inserimento",
  'Options (comma separated for select)': 'Opzioni (separate da virgole per select)',
  Required: 'Obbligatorio',
  'Remove field': 'Rimuovi campo',
};

function createManualTestEditorTranslator(language: string, tr: (value: string) => string) {
  if (language !== 'it') return tr;
  return (phrase: string) => {
    const normalized = phrase.trim();
    if (!normalized) return phrase;
    const globalTranslation = tr(normalized);
    if (globalTranslation !== normalized) return phrase.replace(normalized, globalTranslation);
    const local = manualTestEditorItOverrides[normalized];
    return local ? phrase.replace(normalized, local) : phrase;
  };
}

function createEmptyDefinition(): ManualTestDefinition {
  return {
    code: '',
    name: '',
    description: '',
    category: '',
    valueLabel: '',
    defaultUnit: '',
    requiresImage: false,
    aiPrompt: '',
    fields: [],
  };
}

function normalizeCode(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function createField(): ManualTestFieldDefinition {
  return {
    id: '',
    label: '',
    type: 'text',
    required: false,
    unit: '',
    options: [],
    hint: '',
  };
}

function normalizeDefinition(definition: ManualTestDefinition): ManualTestDefinition {
  return {
    ...definition,
    code: normalizeCode(definition.code || definition.name),
    name: definition.name.trim(),
    description: definition.description?.trim() || '',
    category: definition.category?.trim() || '',
    valueLabel: definition.valueLabel?.trim() || '',
    defaultUnit: definition.defaultUnit?.trim() || '',
    aiPrompt: definition.aiPrompt?.trim() || '',
    fields: definition.fields.map((field) => ({
      ...field,
      id: normalizeCode(field.id || field.label),
      label: field.label.trim(),
      unit: field.unit?.trim() || '',
      hint: field.hint?.trim() || '',
      options: (field.options || []).map((item) => item.trim()).filter(Boolean),
    })),
  };
}

export default function SettingsManualTestEditor() {
  const navigate = useNavigate();
  const { testCode } = useParams<{ testCode: string }>();
  const isNew = !testCode;
  const { fetchJson, fetchApi } = useApiClient();
  const { language, tr } = useI18n();
  const tm = createManualTestEditorTranslator(language, tr);
  const [definitions, setDefinitions] = useState<ManualTestDefinition[]>([]);
  const [draft, setDraft] = useState<ManualTestDefinition>(createEmptyDefinition());
  const [originalCode, setOriginalCode] = useState<string | null>(testCode || null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDefinitions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testCode]);

  async function loadDefinitions() {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchJson<{ definitions: ManualTestDefinition[] }>('/manual-tests/definitions');
      const rows = Array.isArray(data.definitions) ? data.definitions : [];
      setDefinitions(rows);

      if (!testCode) {
        setDraft(createEmptyDefinition());
        setOriginalCode(null);
        return;
      }

      const existing = rows.find((definition) => definition.code === testCode);
      if (!existing) {
        setError(tm('Manual test definition not found.'));
        setDraft(createEmptyDefinition());
        setOriginalCode(null);
        return;
      }

      setDraft({
        ...existing,
        fields: existing.fields.map((field) => ({ ...field, options: [...(field.options || [])] })),
      });
      setOriginalCode(existing.code);
    } catch (err: any) {
      setError(err?.message || tm('Failed to load manual test definitions.'));
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    setError(null);

    if (!draft.name.trim()) {
      setError(tm('Definition name is required.'));
      return;
    }

    const normalized = normalizeDefinition(draft);
    if (!normalized.code) {
      setError(tm('A valid code is required.'));
      return;
    }

    const invalidField = normalized.fields.find((field) => !field.label || !field.id);
    if (invalidField) {
      setError(tm('Each custom field must have both a label and an ID.'));
      return;
    }

    const duplicate = definitions.find((definition) => definition.code === normalized.code && definition.code !== originalCode);
    if (duplicate) {
      setError(tm('A manual test with this code already exists.'));
      return;
    }

    const nextDefinitions = isNew
      ? [...definitions, normalized]
      : [
          ...definitions.filter((definition) => definition.code !== originalCode && definition.code !== normalized.code),
          normalized,
        ];

    setSaving(true);
    try {
      const response = await fetchApi('/manual-tests/definitions', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ definitions: nextDefinitions }),
      });
      if (!response.ok) throw new Error(`Save failed (${response.status})`);

      navigate('/settings/manual-tests', {
        replace: true,
        state: { message: isNew ? tm('Manual test definition created.') : tm('Manual test definition updated.') },
      });
    } catch (err: any) {
      setError(err?.message || tm('Failed to save definitions.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/settings/manual-tests')} className="rounded-full p-2 transition hover:bg-slate-100">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-ink">{isNew ? tm('Add Manual Test') : tm('Edit Manual Test')}</h1>
            <p className="text-sm text-slate-500">
              {isNew
                ? tm('Create a new manual test definition on its own page.')
                : tm('Update the selected manual test definition on its own page.')}
            </p>
          </div>
        </div>
        <button
          className="inline-flex items-center gap-2 rounded-lg bg-ink px-4 py-2 text-white disabled:opacity-60"
          onClick={handleSave}
          disabled={saving || loading || error === tm('Manual test definition not found.')}
        >
          <Save size={16} />
          {saving ? tm('Saving...') : isNew ? tm('Create Test') : tm('Save Changes')}
        </button>
      </div>

      {error ? <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">{error}</div> : null}

      {loading ? (
        <div className="card p-6 text-sm text-slate-500">{tm('Loading definition...')}</div>
      ) : (
        <div className="card space-y-6 p-6">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Input label="Code" value={draft.code} onChange={(value) => setDraft((prev) => ({ ...prev, code: normalizeCode(value) }))} placeholder="keto-stick" />
            <Input label="Name" value={draft.name} onChange={(value) => setDraft((prev) => ({ ...prev, name: value }))} placeholder="Ketone Urine Strip" />
            <Input label="Category" value={draft.category || ''} onChange={(value) => setDraft((prev) => ({ ...prev, category: value }))} placeholder="metabolic" />
            <Input label="Value Label" value={draft.valueLabel || ''} onChange={(value) => setDraft((prev) => ({ ...prev, valueLabel: value }))} placeholder="Ketone Level" />
            <Input label="Default Unit" value={draft.defaultUnit || ''} onChange={(value) => setDraft((prev) => ({ ...prev, defaultUnit: value }))} placeholder="mmol/L" />
            <label className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={Boolean(draft.requiresImage)}
                onChange={(event) => setDraft((prev) => ({ ...prev, requiresImage: event.target.checked }))}
              />
              {tm('Requires image upload')}
            </label>
          </div>

          <Input
            label="Description"
            value={draft.description || ''}
            onChange={(value) => setDraft((prev) => ({ ...prev, description: value }))}
            placeholder="Short usage notes for clinicians"
          />

          <label className="block space-y-1">
            <span className="text-sm text-slate-500">{tm('AI Prompt (optional)')}</span>
            <textarea
              className="min-h-[120px] w-full rounded-lg border border-slate-200 bg-white px-3 py-2"
              value={draft.aiPrompt || ''}
              onChange={(event) => setDraft((prev) => ({ ...prev, aiPrompt: event.target.value }))}
            />
          </label>

          <section className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="text-sm font-semibold text-slate-800">{tm('Custom Fields')}</h2>
                <p className="text-sm text-slate-500">{tm('Configure extra values captured for this manual test.')}</p>
              </div>
              <button
                className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 transition hover:bg-slate-100"
                onClick={() => setDraft((prev) => ({ ...prev, fields: [...prev.fields, createField()] }))}
              >
                <Plus size={14} />
                {tm('Add Field')}
              </button>
            </div>

            {!draft.fields.length ? (
              <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-6 text-sm text-slate-500">
                {tm('No custom fields for this test.')}
              </div>
            ) : (
              <div className="space-y-3">
                {draft.fields.map((field, index) => (
                  <div key={`${field.id || 'field'}-${index}`} className="rounded-xl border border-slate-200 p-4">
                    <div className="grid grid-cols-1 gap-3 xl:grid-cols-4">
                      <Input
                        label="Field ID"
                        value={field.id}
                        onChange={(value) =>
                          setDraft((prev) => ({
                            ...prev,
                            fields: prev.fields.map((row, rowIdx) => (rowIdx === index ? { ...row, id: normalizeCode(value) } : row)),
                          }))
                        }
                        placeholder="reading-label"
                      />
                      <Input
                        label="Label"
                        value={field.label}
                        onChange={(value) =>
                          setDraft((prev) => ({
                            ...prev,
                            fields: prev.fields.map((row, rowIdx) => (rowIdx === index ? { ...row, label: value } : row)),
                          }))
                        }
                        placeholder="Strip Label"
                      />
                      <Select
                        label="Type"
                        value={field.type}
                        options={['text', 'number', 'select', 'date', 'textarea']}
                        onChange={(value) =>
                          setDraft((prev) => ({
                            ...prev,
                            fields: prev.fields.map((row, rowIdx) =>
                              rowIdx === index ? { ...row, type: value as ManualTestFieldDefinition['type'] } : row,
                            ),
                          }))
                        }
                      />
                      <Input
                        label="Unit"
                        value={field.unit || ''}
                        onChange={(value) =>
                          setDraft((prev) => ({
                            ...prev,
                            fields: prev.fields.map((row, rowIdx) => (rowIdx === index ? { ...row, unit: value } : row)),
                          }))
                        }
                        placeholder="mmol/L"
                      />
                    </div>

                    <div className="mt-3 grid grid-cols-1 gap-3 xl:grid-cols-2">
                      <Input
                        label="Hint"
                        value={field.hint || ''}
                        onChange={(value) =>
                          setDraft((prev) => ({
                            ...prev,
                            fields: prev.fields.map((row, rowIdx) => (rowIdx === index ? { ...row, hint: value } : row)),
                          }))
                        }
                        placeholder="Shown to staff during entry"
                      />
                      <Input
                        label="Options (comma separated for select)"
                        value={(field.options || []).join(', ')}
                        onChange={(value) =>
                          setDraft((prev) => ({
                            ...prev,
                            fields: prev.fields.map((row, rowIdx) =>
                              rowIdx === index
                                ? { ...row, options: value.split(',').map((item) => item.trim()).filter(Boolean) }
                                : row,
                            ),
                          }))
                        }
                        placeholder="Negative, Trace, Moderate, Large"
                      />
                    </div>

                    <div className="mt-3 flex items-center justify-between gap-3">
                      <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                        <input
                          type="checkbox"
                          checked={Boolean(field.required)}
                          onChange={(event) =>
                            setDraft((prev) => ({
                              ...prev,
                              fields: prev.fields.map((row, rowIdx) =>
                                rowIdx === index ? { ...row, required: event.target.checked } : row,
                              ),
                            }))
                          }
                        />
                        {tm('Required')}
                      </label>
                      <button
                        className="inline-flex items-center gap-1 text-sm text-rose-700 transition hover:text-rose-800"
                        onClick={() =>
                          setDraft((prev) => ({
                            ...prev,
                            fields: prev.fields.filter((_, rowIdx) => rowIdx !== index),
                          }))
                        }
                      >
                        <Trash2 size={14} />
                        {tm('Remove field')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

function Input({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  const { language, tr } = useI18n();
  const tm = createManualTestEditorTranslator(language, tr);
  return (
    <label className="block space-y-1">
      <span className="text-sm text-slate-500">{tm(label)}</span>
      <input
        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder ? tm(placeholder) : undefined}
      />
    </label>
  );
}

function Select({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
}) {
  const { language, tr } = useI18n();
  const tm = createManualTestEditorTranslator(language, tr);
  return (
    <label className="block space-y-1">
      <span className="text-sm text-slate-500">{tm(label)}</span>
      <SelectMenu
        value={value}
        onChange={onChange}
        buttonAriaLabel={tm(label)}
        triggerClassName="w-full rounded-lg border border-slate-200 bg-white px-3 py-2"
        options={options.map((option) => ({ value: option, label: tm(option) }))}
      />
    </label>
  );
}
